import React, { useState, useEffect, useRef } from "react";
import { RefreshCw, Tag, ChevronDown, ChevronUp, Plus, Edit, Trash2, Package } from "lucide-react";
import { configService } from "../services/configService";
import toast from "react-hot-toast";
import CustomerShipmentScheduler from "../components/CustomerShipmentScheduler";
import { formatDate } from "../utils/dateUtils";
import { isTokenInvalidationError, isLoggingOut } from "../utils/errorUtils";

interface SyncStatus {
  total_shipments: number;
  synced_shipments: number;
  pending_sync: number;
  failed?: number;
  last_synced_at: string | null;
  is_syncing?: boolean;
}

interface SyncLogItem {
  id: number;
  customer_shipment_id: number | null;
  order_number: string;
  customer_name: string;
  action: string;
  sync_direction: string;
  status: string;
  error_message?: string | null;
  changed_fields?: string | null;
  request_data?: string | null;
  response_data?: string | null;
  synced_at: string | null;
  created_at: string | null;
  mode: string;
}

interface CustomerShipmentItem {
  id: number;
  mode: string;
  order_number: string;
  customer_name: string | null;
  tracking_number: string | null;
  shiphero_order_id: number | null;
  shiphero_legacy_id?: number | string | null;
  shiphero_order_number?: string | null;
  sync_status: string;
  sync_error?: string | null;
  retry_count?: number;
  needs_manual_retry?: boolean;
  shipment_date?: string | null;
  acknowledged_at?: string | null;
  allocated_at?: string | null;
  packed_at?: string | null;
  shipped_at?: string | null;
  delivered_at?: string | null;
  // Derived flags from backend for UI badges
  failed?: boolean;
  acknowledged?: boolean;
  allocated?: boolean;
  packed?: boolean;
  shipped?: boolean;
  delivered?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

/** Form row for channel xref (tags as comma-separated string in UI). */
interface ChannelMappingFormRow {
  id?: number;
  channel: string;
  tags: string;
  shipping_name: string;
  store_profile: string;
  account_number: string;
  account_zip: string;
  bill: string;
  additional_packing_notes: string;
  default_carrier: string;
  default_ship_method: string;
}

/** Form row for address account xref (customer.name + delivery_address.code → Account Number, Account Zip, Bill). */
interface AddressAccountMappingFormRow {
  id?: number;
  customer: string;
  address_code: string;
  account_number: string;
  account_zip: string;
  bill: string;
  tags: string;
  additional_packing_notes: string;
}

const CustomerShipmentsPage: React.FC = () => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    total_shipments: 0,
    synced_shipments: 0,
    pending_sync: 0,
    failed: 0,
    last_synced_at: null,
    is_syncing: false,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRunningSync, setIsRunningSync] = useState(false);
  const [syncMode, setSyncMode] = useState<"live" | "test">("live");
  const [isModeLoading, setIsModeLoading] = useState(true);
  const [isUpdatingMode, setIsUpdatingMode] = useState(false);
  const [perPage] = useState(20);
  const [query, setQuery] = useState("");

  // Shipments listing state
  const [shipments, setShipments] = useState<CustomerShipmentItem[]>([]);
  const [shipmentsPage, setShipmentsPage] = useState(1);
  const [shipmentsTotalPages, setShipmentsTotalPages] = useState(1);
  const [isShipmentsLoading, setIsShipmentsLoading] = useState(true);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyOrderNumber, setHistoryOrderNumber] = useState<string | null>(null);
  const [historyLogs, setHistoryLogs] = useState<SyncLogItem[]>([]);
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  // Button loading states
  const [loadingRetrySync, setLoadingRetrySync] = useState<Set<number>>(new Set());
  const [loadingSyncNow, setLoadingSyncNow] = useState<Set<string>>(new Set());
  const prevQueryRef = useRef(query);
  const autoRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [channelTagMappings, setChannelTagMappings] = useState<ChannelMappingFormRow[]>([]);
  const [channelTagMappingsOriginal, setChannelTagMappingsOriginal] = useState<ChannelMappingFormRow[]>([]);
  const [isChannelTagsExpanded, setIsChannelTagsExpanded] = useState(false);
  const [isChannelTagsLoading, setIsChannelTagsLoading] = useState(false);
  const [isSavingChannelTags, setIsSavingChannelTags] = useState(false);
  const [editingChannelTagId, setEditingChannelTagId] = useState<number | null>(null);
  const [editSnapshot, setEditSnapshot] = useState<ChannelMappingFormRow | null>(null);
  const [channelTagDeletePending, setChannelTagDeletePending] = useState<{
    index: number;
    row: ChannelMappingFormRow;
  } | null>(null);

  const [addressAccountMappings, setAddressAccountMappings] = useState<AddressAccountMappingFormRow[]>([]);
  const [addressAccountMappingsOriginal, setAddressAccountMappingsOriginal] = useState<AddressAccountMappingFormRow[]>([]);
  const [isAddressAccountExpanded, setIsAddressAccountExpanded] = useState(false);
  const [isAddressAccountLoading, setIsAddressAccountLoading] = useState(false);
  const [isSavingAddressAccount, setIsSavingAddressAccount] = useState(false);
  const [editingAddressAccountId, setEditingAddressAccountId] = useState<number | null>(null);
  const [editSnapshotAddressAccount, setEditSnapshotAddressAccount] = useState<AddressAccountMappingFormRow | null>(null);
  const [addressAccountDeletePending, setAddressAccountDeletePending] = useState<{
    index: number;
    row: AddressAccountMappingFormRow;
  } | null>(null);

  useEffect(() => {
    loadSyncStatus();
    loadSyncMode();
    loadChannelTagMappings();
    loadAddressAccountMappings();
  }, []);

  useEffect(() => {
    if (channelTagDeletePending != null) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [channelTagDeletePending]);

  useEffect(() => {
    if (addressAccountDeletePending != null) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [addressAccountDeletePending]);

  useEffect(() => {
    // Load shipments after sync mode has been loaded
    if (syncMode && !isModeLoading) {
      const didQueryChange = prevQueryRef.current !== query;
      prevQueryRef.current = query;
      if (didQueryChange && shipmentsPage !== 1) {
        setShipmentsPage(1);
        return;
      }

      loadShipments(shipmentsPage, query);
    }
  }, [shipmentsPage, query, syncMode, isModeLoading]);

  // Monitor mode changes and reload when mode changes
  useEffect(() => {
    if (syncMode && !isModeLoading) {
      setShipmentsPage(1);
      loadShipments(1, query);
      loadSyncStatus();
    }
  }, [syncMode]);

  // Auto-refresh during sync operations
  useEffect(() => {
    const isSyncing = isRunningSync || Boolean(syncStatus.is_syncing);

    if (!isSyncing) {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
      return;
    }

    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
      autoRefreshIntervalRef.current = null;
    }

    autoRefreshIntervalRef.current = setInterval(async () => {
      await loadSyncStatus();
      if (syncMode && !isModeLoading) {
        await loadShipments(shipmentsPage, query);
      }
    }, 5000);

    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    };
  }, [isRunningSync, syncStatus.is_syncing, syncMode, isModeLoading, shipmentsPage, query]);

  const loadSyncStatus = async () => {
    try {
      const status = await configService.getCustomerShipmentSyncStatus();
      // Normalize the response to ensure proper keys are available
      setSyncStatus({
        total_shipments: status.total_shipments ?? 0,
        synced_shipments: status.synced_shipments ?? 0,
        pending_sync: status.pending_sync ?? 0,
        failed: status.failed ?? 0,
        last_synced_at: status.last_synced_at ?? null,
        is_syncing: status.is_syncing ?? false,
      });
    } catch (error) {
      console.error("Failed to load customer shipment sync status:", error);
    }
  };

  const loadChannelTagMappings = async () => {
    try {
      setIsChannelTagsLoading(true);
      const result = await configService.getCustomerShipmentChannelTagMappings();
      const mappings = result.mappings || [];
      const rows = mappings.map((m: any) => ({
        id: m.id,
        channel: m.channel || "",
        tags: Array.isArray(m.tags) ? m.tags.join(", ") : (m.tags ?? ""),
        shipping_name: m.shipping_name ?? "",
        store_profile: m.store_profile ?? "",
        account_number: m.account_number ?? "",
        account_zip: m.account_zip ?? "",
        bill: m.bill ?? "",
        additional_packing_notes: m.additional_packing_notes ?? "",
        default_carrier: m.default_carrier ?? "",
        default_ship_method: m.default_ship_method ?? "",
      }));
      setChannelTagMappings(rows);
      setChannelTagMappingsOriginal(rows.map((r) => ({ ...r })));
      setEditingChannelTagId(null);
      setEditSnapshot(null);
    } catch (error) {
      console.error("Failed to load customer shipment channel tag mappings:", error);
    } finally {
      setIsChannelTagsLoading(false);
    }
  };

  const loadAddressAccountMappings = async () => {
    try {
      setIsAddressAccountLoading(true);
      const result = await configService.getCustomerShipmentAddressAccountMappings();
      const mappings = result.mappings || [];
      const rows = mappings.map((m: any) => ({
        id: m.id,
        customer: m.customer ?? "",
        address_code: m.address_code ?? "",
        account_number: m.account_number ?? "",
        account_zip: m.account_zip ?? "",
        bill: m.bill ?? "",
        tags: m.tags ?? "",
        additional_packing_notes: m.additional_packing_notes ?? "",
      }));
      setAddressAccountMappings(rows);
      setAddressAccountMappingsOriginal(rows.map((r) => ({ ...r })));
      setEditingAddressAccountId(null);
      setEditSnapshotAddressAccount(null);
    } catch (error) {
      console.error("Failed to load customer shipment address account mappings:", error);
    } finally {
      setIsAddressAccountLoading(false);
    }
  };

  const loadShipments = async (pageNum: number, q?: string) => {
    try {
      setIsShipmentsLoading(true);
      const res = await configService.listCustomerShipments(pageNum, perPage, q, syncMode);
      setShipments(res.items);
      setShipmentsTotalPages(res.total_pages || 1);
    } catch (error) {
      console.error("Failed to load customer shipments:", error);
    } finally {
      setIsShipmentsLoading(false);
    }
  };

  const loadSyncMode = async () => {
    try {
      setIsModeLoading(true);
      const modeConfig = await configService.getCustomerShipmentSyncMode();
      setSyncMode(modeConfig.mode);
    } catch (error) {
      console.error("Failed to load sync mode:", error);
    } finally {
      setIsModeLoading(false);
    }
  };

  const handleModeChange = async (newMode: "live" | "test") => {
    if (newMode === syncMode) return;
    try {
      setIsUpdatingMode(true);
      await configService.updateCustomerShipmentSyncMode(newMode);
      setSyncMode(newMode);
      toast.success(`Customer shipment sync mode set to ${newMode.toUpperCase()}`);
      // Reload shipments for the new mode
      setShipmentsPage(1);
      await loadShipments(1, query);
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message || "Failed to update sync mode");
      }
      console.error("Failed to update sync mode:", error);
    } finally {
      setIsUpdatingMode(false);
    }
  };

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error("Please enter a value to search");
      return;
    }

    try {
      setShipmentsPage(1);
      await loadShipments(1, query);
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message || "Failed to search shipments");
      }
      console.error("Search error:", error);
    }
  };

  const clearSearch = async () => {
    try {
      setQuery("");
      setShipmentsPage(1);
      await loadShipments(1, "");
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message || "Failed to load shipments");
      }
      console.error("Clear search error:", error);
    }
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await loadSyncStatus();
      await loadShipments(shipmentsPage, query);
    } catch (error) {
      console.error("Failed to refresh data:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const emptyChannelMappingRow = (): ChannelMappingFormRow => ({
    id: undefined,
    channel: "",
    tags: "",
    shipping_name: "",
    store_profile: "",
    account_number: "",
    account_zip: "",
    bill: "",
    additional_packing_notes: "",
    default_carrier: "",
    default_ship_method: "",
  });

  const handleChannelTagRowChange = (
    index: number,
    field: keyof ChannelMappingFormRow,
    value: string
  ) => {
    setChannelTagMappings((prev) => {
      const next = [...prev];
      if (!next[index]) {
        next[index] = emptyChannelMappingRow();
      }
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addChannelTagRow = () => {
    setChannelTagMappings((prev) => [...prev, emptyChannelMappingRow()]);
  };

  const startEditChannelTag = (row: ChannelMappingFormRow) => {
    if (row.id == null) return;
    setEditingChannelTagId(row.id);
    setEditSnapshot({ ...row });
  };

  const cancelEditChannelTag = () => {
    if (editingChannelTagId == null || editSnapshot == null) return;
    setChannelTagMappings((prev) =>
      prev.map((m) => (m.id === editingChannelTagId ? { ...editSnapshot } : m))
    );
    setEditingChannelTagId(null);
    setEditSnapshot(null);
  };

  const requestRemoveChannelTagRow = (index: number) => {
    const row = channelTagMappings[index];
    if (row) setChannelTagDeletePending({ index, row });
  };

  const cancelRemoveChannelTagRow = () => {
    setChannelTagDeletePending(null);
  };

  const confirmRemoveChannelTagRow = async () => {
    if (channelTagDeletePending == null) return;
    const { index, row } = channelTagDeletePending;
    setChannelTagDeletePending(null);
    try {
      const nextMappings = channelTagMappings.filter((_, i) => i !== index);
      if (row.id != null) {
        setChannelTagMappings(nextMappings);
        setIsSavingChannelTags(true);
        await configService.patchCustomerShipmentChannelTagMappings({ deleted: [row.id] });
        toast.success("Channel mapping deleted");
        await loadChannelTagMappings();
        if (row.id === editingChannelTagId) {
          setEditingChannelTagId(null);
          setEditSnapshot(null);
        }
      } else {
        setChannelTagMappings(nextMappings);
      }
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(
          error?.message || "Failed to delete channel mapping"
        );
      }
    } finally {
      setIsSavingChannelTags(false);
    }
  };

  const saveChannelTagMappings = async () => {
    try {
      setIsSavingChannelTags(true);
      const currentIds = new Set(
        channelTagMappings.filter((m) => m.id != null).map((m) => m.id as number)
      );
      const added = channelTagMappings
        .filter((row) => row.id == null && (row.channel || "").trim() !== "")
        .map((row) => ({
          channel: row.channel.trim(),
          tags: row.tags
            .split(",")
            .map((t) => t.trim())
            .filter((t) => t.length > 0),
          shipping_name: row.shipping_name?.trim() || undefined,
          store_profile: row.store_profile?.trim() || undefined,
          account_number: row.account_number?.trim() || undefined,
          account_zip: row.account_zip?.trim() || undefined,
          bill: row.bill?.trim() || undefined,
          additional_packing_notes: row.additional_packing_notes?.trim() || undefined,
          default_carrier: row.default_carrier?.trim() || undefined,
          default_ship_method: row.default_ship_method?.trim() || undefined,
        }));
      const deleted = channelTagMappingsOriginal
        .filter((m) => m.id != null && !currentIds.has(m.id!))
        .map((m) => m.id as number);
      const byId = new Map(
        channelTagMappingsOriginal
          .filter((m) => m.id != null)
          .map((m) => [m.id!, m] as [number, ChannelMappingFormRow])
      );
      const updated = channelTagMappings
        .filter((row) => row.id != null && (row.channel || "").trim() !== "")
        .filter((row) => {
          const orig = byId.get(row.id!);
          if (!orig) return true;
          const tagsStr = (s: string) =>
            s
              .split(",")
              .map((t) => t.trim())
              .filter((t) => t.length > 0)
              .join(",");
          return (
            (row.channel || "").trim() !== (orig.channel || "").trim() ||
            tagsStr(row.tags) !== tagsStr(orig.tags) ||
            (row.shipping_name || "").trim() !== (orig.shipping_name || "").trim() ||
            (row.store_profile || "").trim() !== (orig.store_profile || "").trim() ||
            (row.account_number || "").trim() !== (orig.account_number || "").trim() ||
            (row.account_zip || "").trim() !== (orig.account_zip || "").trim() ||
            (row.bill || "").trim() !== (orig.bill || "").trim() ||
            (row.additional_packing_notes || "").trim() !== (orig.additional_packing_notes || "").trim() ||
            (row.default_carrier || "").trim() !== (orig.default_carrier || "").trim() ||
            (row.default_ship_method || "").trim() !== (orig.default_ship_method || "").trim()
          );
        })
        .map((row) => ({
          id: row.id,
          channel: row.channel.trim(),
          tags: row.tags
            .split(",")
            .map((t) => t.trim())
            .filter((t) => t.length > 0),
          shipping_name: row.shipping_name?.trim() || undefined,
          store_profile: row.store_profile?.trim() || undefined,
          account_number: row.account_number?.trim() || undefined,
          account_zip: row.account_zip?.trim() || undefined,
          bill: row.bill?.trim() || undefined,
          additional_packing_notes: row.additional_packing_notes?.trim() || undefined,
          default_carrier: row.default_carrier?.trim() || undefined,
          default_ship_method: row.default_ship_method?.trim() || undefined,
        }));

      if (added.length === 0 && updated.length === 0 && deleted.length === 0) {
        toast.success("No changes to save");
        setIsSavingChannelTags(false);
        return;
      }
      await configService.patchCustomerShipmentChannelTagMappings({
        added: added.length ? added : undefined,
        updated: updated.length ? updated : undefined,
        deleted: deleted.length ? deleted : undefined,
      });
      toast.success("Customer shipment channel mappings updated");
      await loadChannelTagMappings();
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(
          error?.message || "Failed to update customer shipment channel mappings"
        );
      }
    } finally {
      setIsSavingChannelTags(false);
    }
  };

  const emptyAddressAccountRow = (): AddressAccountMappingFormRow => ({
    id: undefined,
    customer: "",
    address_code: "",
    account_number: "",
    account_zip: "",
    bill: "",
    tags: "",
    additional_packing_notes: "",
  });

  const handleAddressAccountRowChange = (
    index: number,
    field: keyof AddressAccountMappingFormRow,
    value: string
  ) => {
    setAddressAccountMappings((prev) => {
      const next = [...prev];
      if (!next[index]) {
        next[index] = emptyAddressAccountRow();
      }
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addAddressAccountRow = () => {
    setAddressAccountMappings((prev) => [...prev, emptyAddressAccountRow()]);
  };

  const startEditAddressAccount = (row: AddressAccountMappingFormRow) => {
    if (row.id == null) return;
    setEditingAddressAccountId(row.id);
    setEditSnapshotAddressAccount({ ...row });
  };

  const cancelEditAddressAccount = () => {
    if (editingAddressAccountId == null || editSnapshotAddressAccount == null) return;
    setAddressAccountMappings((prev) =>
      prev.map((m) => (m.id === editingAddressAccountId ? { ...editSnapshotAddressAccount } : m))
    );
    setEditingAddressAccountId(null);
    setEditSnapshotAddressAccount(null);
  };

  const requestRemoveAddressAccountRow = (index: number) => {
    const row = addressAccountMappings[index];
    if (row) setAddressAccountDeletePending({ index, row });
  };

  const cancelRemoveAddressAccountRow = () => {
    setAddressAccountDeletePending(null);
  };

  const confirmRemoveAddressAccountRow = async () => {
    if (addressAccountDeletePending == null) return;
    const { index, row } = addressAccountDeletePending;
    setAddressAccountDeletePending(null);
    try {
      const nextMappings = addressAccountMappings.filter((_, i) => i !== index);
      if (row.id != null) {
        setAddressAccountMappings(nextMappings);
        setIsSavingAddressAccount(true);
        await configService.patchCustomerShipmentAddressAccountMappings({ deleted: [row.id] });
        toast.success("Address account mapping deleted");
        await loadAddressAccountMappings();
        if (row.id === editingAddressAccountId) {
          setEditingAddressAccountId(null);
          setEditSnapshotAddressAccount(null);
        }
      } else {
        setAddressAccountMappings(nextMappings);
      }
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(
          error?.message || "Failed to delete address account mapping"
        );
      }
    } finally {
      setIsSavingAddressAccount(false);
    }
  };

  const saveAddressAccountMappings = async () => {
    try {
      setIsSavingAddressAccount(true);
      const currentIds = new Set(
        addressAccountMappings.filter((m) => m.id != null).map((m) => m.id as number)
      );
      const added = addressAccountMappings
        .filter((row) => row.id == null && (row.customer || "").trim() !== "" && (row.address_code || "").trim() !== "")
        .map((row) => ({
          customer: row.customer.trim(),
          address_code: row.address_code.trim(),
          account_number: row.account_number?.trim() || undefined,
          account_zip: row.account_zip?.trim() || undefined,
          bill: row.bill?.trim() || undefined,
          tags: row.tags?.trim() || undefined,
          additional_packing_notes: row.additional_packing_notes?.trim() || undefined,
        }));
      const deleted = addressAccountMappingsOriginal
        .filter((m) => m.id != null && !currentIds.has(m.id!))
        .map((m) => m.id as number);
      const byId = new Map(
        addressAccountMappingsOriginal
          .filter((m) => m.id != null)
          .map((m) => [m.id!, m] as [number, AddressAccountMappingFormRow])
      );
      const updated = addressAccountMappings
        .filter((row) => row.id != null && (row.customer || "").trim() !== "" && (row.address_code || "").trim() !== "")
        .filter((row) => {
          const orig = byId.get(row.id!);
          if (!orig) return true;
          return (
            (row.customer || "").trim() !== (orig.customer || "").trim() ||
            (row.address_code || "").trim() !== (orig.address_code || "").trim() ||
            (row.account_number || "").trim() !== (orig.account_number || "").trim() ||
            (row.account_zip || "").trim() !== (orig.account_zip || "").trim() ||
            (row.bill || "").trim() !== (orig.bill || "").trim() ||
            (row.tags || "").trim() !== (orig.tags || "").trim() ||
            (row.additional_packing_notes || "").trim() !== (orig.additional_packing_notes || "").trim()
          );
        })
        .map((row) => ({
          id: row.id,
          customer: row.customer.trim(),
          address_code: row.address_code.trim(),
          account_number: row.account_number?.trim() || undefined,
          account_zip: row.account_zip?.trim() || undefined,
          bill: row.bill?.trim() || undefined,
          tags: row.tags?.trim() || undefined,
          additional_packing_notes: row.additional_packing_notes?.trim() || undefined,
        }));

      if (added.length === 0 && updated.length === 0 && deleted.length === 0) {
        toast.success("No changes to save");
        setIsSavingAddressAccount(false);
        return;
      }
      await configService.patchCustomerShipmentAddressAccountMappings({
        added: added.length ? added : undefined,
        updated: updated.length ? updated : undefined,
        deleted: deleted.length ? deleted : undefined,
      });
      toast.success("Address account mappings updated");
      await loadAddressAccountMappings();
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(
          error?.message || "Failed to update address account mappings"
        );
      }
    } finally {
      setIsSavingAddressAccount(false);
    }
  };

  const handleRunNow = async () => {
    setIsRunningSync(true);
    try {
      const res = await configService.runCustomerShipmentSync();
      toast.success(res.message || "Customer shipment sync completed");
      await loadSyncStatus();
      await loadShipments(shipmentsPage, query);
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message || "Failed to run customer shipment sync");
      }
    } finally {
      setIsRunningSync(false);
    }
  };

  const openHistory = async (orderNumber: string) => {
    try {
      setShowHistoryModal(true);
      setHistoryOrderNumber(orderNumber);
      setExpandedLogs(new Set());
      setIsHistoryLoading(true);
      const res = await configService.getCustomerShipmentHistory(orderNumber, syncMode);
      setHistoryLogs(res.items as any);
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message || "Failed to load shipment history");
      }
      setShowHistoryModal(false);
      setHistoryOrderNumber(null);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const retryShipmentSync = async (shipmentId: number) => {
    // Add to loading set
    setLoadingRetrySync(prev => new Set(prev).add(shipmentId));

    try {
      const res = await configService.retryCustomerShipmentSync(shipmentId);
      if ((res as any).status === "error") {
        toast.error((res as any).message || "Manual retry failed");
      } else {
        toast.success((res as any).message || "Manual retry completed successfully");
      }
      await refreshData();
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message || "Failed to retry shipment sync");
      }
    } finally {
      // Remove from loading set
      setLoadingRetrySync(prev => {
        const newSet = new Set(prev);
        newSet.delete(shipmentId);
        return newSet;
      });
    }
  };

  const manualSyncStatus = async (orderNumber: string) => {
    // Add to loading set
    setLoadingSyncNow(prev => new Set(prev).add(orderNumber));

    try {
      const res = await configService.syncCustomerShipmentStatus(orderNumber);
      if ((res as any).status === "error") {
        toast.error((res as any).message || "Manual status sync failed");
      } else {
        toast.success((res as any).message || "Manual status sync completed successfully");
      }
      await refreshData();
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message || "Failed to sync shipment status");
      }
    } finally {
      // Remove from loading set
      setLoadingSyncNow(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderNumber);
        return newSet;
      });
    }
  };


  // Formatting helpers for cleaner labels in the history modal
  const toTitleCase = (s: string) => {
    if (!s) return s;
    return s
      .replace(/[_-]+/g, " ")
      .split(" ")
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(" ");
  };

  const formatDirection = (d?: string) => {
    if (!d) return "";
    if (d === "fulfil_to_shiphero") return "Fulfil → ShipHero";
    if (d === "shiphero_to_fulfil") return "ShipHero → Fulfil";
    return toTitleCase(d);
  };

  const formatStatus = (s?: string) => {
    if (!s) return "";
    return toTitleCase(s);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Shipments</h1>
          <p className="mt-2 text-sm text-gray-700">
            Monitor synchronization of customer shipments between Fulfil and ShipHero
          </p>
        </div>
        {/* Actions Bar */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          {/* Mode control */}
          <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-md border">
            <span className="text-sm font-medium text-gray-700">Mode</span>
            {isModeLoading ? (
              <div className="inline-flex items-center px-3 py-1.5 text-sm text-gray-500">
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </div>
            ) : (
              <div className="inline-flex rounded border overflow-hidden">
                <button
                  onClick={() => handleModeChange("live")}
                  disabled={isUpdatingMode || isRunningSync || syncStatus.is_syncing}
                  className={`${
                    syncMode === "live"
                      ? "bg-primary-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  } px-3 py-1.5 text-sm font-medium focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Live
                </button>
                <button
                  onClick={() => handleModeChange("test")}
                  disabled={isUpdatingMode || isRunningSync || syncStatus.is_syncing}
                  className={`${
                    syncMode === "test"
                      ? "bg-primary-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  } px-3 py-1.5 text-sm font-medium border-l focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Test
                </button>
              </div>
            )}
            {isUpdatingMode && (
              <RefreshCw className="ml-1 h-4 w-4 animate-spin text-primary-600" />
            )}
          </div>

          <button
            onClick={handleRunNow}
            disabled={isRunningSync || syncStatus.is_syncing}
            className={`inline-flex items-center justify-center px-3 py-2 text-sm rounded-md border ${
              isRunningSync || syncStatus.is_syncing
                ? "border-yellow-600 text-yellow-700 bg-yellow-50"
                : "border-primary-600 text-primary-700 bg-white hover:bg-primary-50"
            } disabled:opacity-50`}
          >
            {isRunningSync || syncStatus.is_syncing ? (
              <span className="inline-flex items-center">
                <RefreshCw className="h-5 w-5 mr-2 animate-spin" /> Sync Running...
              </span>
            ) : (
              <span>Run Now</span>
            )}
          </button>

          <button
            onClick={refreshData}
            disabled={isRefreshing}
            className="inline-flex items-center px-3 py-2 rounded-md text-sm border bg-white hover:bg-gray-50"
          >
            <RefreshCw className={`h-5 w-5 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Sync Scheduler section */}
      <CustomerShipmentScheduler />

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Tag className="h-5 w-5 text-primary-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                Channel Mapping
              </h3>
              <p className="text-sm text-gray-600">
                Define which channels belong to this workflow. Match on Channel Name; set shipping name, store profile, tags, and packing note extras (Account, Account Zip, Bill, Additional Notes).
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsChannelTagsExpanded((prev) => !prev)}
            className="text-gray-500 hover:text-gray-700"
          >
            {isChannelTagsExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        </div>

        {isChannelTagsExpanded && (
          <div className="mt-4 space-y-4">
            <div className="mb-2 text-sm text-gray-600">
              <p>
                Select a channel and enter one or more tags separated by commas.
              </p>
              <p className="mt-1">
                Example: Channel{" "}
                <span className="font-semibold">Shopify</span> with tags{" "}
                <span className="font-mono">D2C, Small Parcel</span>.
              </p>
            </div>

            {isChannelTagsLoading ? (
              <div className="flex items-center justify-center h-24">
                <RefreshCw className="h-6 w-6 text-primary-600 animate-spin" />
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Channel
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Shipping Name
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Store Profile
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Tags (comma)
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Account Number
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Account Zip
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Bill
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Default Carrier
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Default Method
                      </th>

                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-28">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {channelTagMappings.length === 0 ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-4 py-4 text-sm text-gray-500 text-center"
                        >
                          No mappings configured. Add a row to get started.
                        </td>
                      </tr>
                    ) : (
                      channelTagMappings.map((row, index) => {
                        const hasId = row.id != null;
                        const isEditing = hasId && editingChannelTagId === row.id;
                        const isDisabled = hasId && !isEditing;
                        const inputClass =
                          "block w-full min-w-[80px] rounded border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed";
                        return (
                          <tr key={row.id ?? `new-${index}`}>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.channel}
                                onChange={(e) =>
                                  handleChannelTagRowChange(index, "channel", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Channel"
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.shipping_name}
                                onChange={(e) =>
                                  handleChannelTagRowChange(index, "shipping_name", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Shipping Name"
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.store_profile}
                                onChange={(e) =>
                                  handleChannelTagRowChange(index, "store_profile", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Profile"
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.tags}
                                onChange={(e) =>
                                  handleChannelTagRowChange(index, "tags", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Tag1, Tag2, ..."
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.account_number}
                                onChange={(e) =>
                                  handleChannelTagRowChange(index, "account_number", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Account Number"
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.account_zip}
                                onChange={(e) =>
                                  handleChannelTagRowChange(index, "account_zip", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Zip"
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.bill}
                                onChange={(e) =>
                                  handleChannelTagRowChange(index, "bill", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Bill"
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.default_carrier}
                                onChange={(e) =>
                                  handleChannelTagRowChange(index, "default_carrier", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Carrier"
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.default_ship_method}
                                onChange={(e) =>
                                  handleChannelTagRowChange(index, "default_ship_method", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Method"
                                className={inputClass}
                              />
                            </td>

                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {hasId ? (
                                isEditing ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={cancelEditChannelTag}
                                      className="text-gray-600 hover:text-gray-900 mr-2"
                                      title="Cancel"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => requestRemoveChannelTagRow(index)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Remove row"
                                    >
                                      <Trash2 className="h-4 w-4 inline" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => startEditChannelTag(row)}
                                      className="text-primary-600 hover:text-primary-900 mr-2"
                                      title="Edit row"
                                    >
                                      <Edit className="h-4 w-4 inline" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => requestRemoveChannelTagRow(index)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Remove row"
                                    >
                                      <Trash2 className="h-4 w-4 inline" />
                                    </button>
                                  </>
                                )
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => requestRemoveChannelTagRow(index)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Remove row"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={addChannelTagRow}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Mapping
              </button>
              <button
                type="button"
                onClick={saveChannelTagMappings}
                disabled={isSavingChannelTags}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isSavingChannelTags ? "Saving..." : "Save Mappings"}
              </button>
            </div>

            {channelTagDeletePending != null && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
                role="dialog"
                aria-modal="true"
                aria-labelledby="channel-mapping-delete-title"
              >
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-5">
                  <h3
                    id="channel-mapping-delete-title"
                    className="text-lg font-medium text-gray-900 mb-2"
                  >
                    Delete channel mapping?
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    <span className="font-mono font-medium">
                      {channelTagDeletePending.row.channel || "(empty)"}
                    </span>
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={cancelRemoveChannelTagRow}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmRemoveChannelTagRow}
                      disabled={isSavingChannelTags}
                      className="px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingChannelTags ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Address Shipping Account (Customer + Address Code → Account Number, Account Zip, Bill) */}
      <div className="bg-white rounded-lg shadow-md p-6 mt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Package className="h-5 w-5 text-primary-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                Address Shipping Account
              </h3>
              <p className="text-sm text-gray-600">
                Per Ship-To address: match Customer and Address Code. If a row matches the order, Account Number, Account Zip, and Bill from this table are used on the packing note instead of Channel Mapping.
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsAddressAccountExpanded((prev) => !prev)}
            className="text-gray-500 hover:text-gray-700"
          >
            {isAddressAccountExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        </div>

        {isAddressAccountExpanded && (
          <div className="mt-4 space-y-4">
            <div className="mb-2 text-sm text-gray-600">
              <p>
                Customer and Address Code are matched from the order. Packing note will show: Account Number:, Account Zip:, Bill: below shipping instructions.
              </p>
            </div>

            {isAddressAccountLoading ? (
              <div className="flex items-center justify-center h-24">
                <RefreshCw className="h-6 w-6 text-primary-600 animate-spin" />
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Customer
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Address Code
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Account Number
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Account Zip
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Bill
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Tags
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Additional Packing Notes
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-28">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {addressAccountMappings.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-4 text-sm text-gray-500 text-center"
                        >
                          No mappings configured. Add a row to get started.
                        </td>
                      </tr>
                    ) : (
                      addressAccountMappings.map((row, index) => {
                        const hasId = row.id != null;
                        const isEditing = hasId && editingAddressAccountId === row.id;
                        const isDisabled = hasId && !isEditing;
                        const inputClass =
                          "block w-full min-w-[80px] rounded border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed";
                        return (
                          <tr key={row.id ?? `new-${index}`}>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.customer}
                                onChange={(e) =>
                                  handleAddressAccountRowChange(index, "customer", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Customer"
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.address_code}
                                onChange={(e) =>
                                  handleAddressAccountRowChange(index, "address_code", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Address Code"
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.account_number}
                                onChange={(e) =>
                                  handleAddressAccountRowChange(index, "account_number", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Account Number"
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.account_zip}
                                onChange={(e) =>
                                  handleAddressAccountRowChange(index, "account_zip", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Zip"
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.bill}
                                onChange={(e) =>
                                  handleAddressAccountRowChange(index, "bill", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Bill"
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.tags}
                                onChange={(e) =>
                                  handleAddressAccountRowChange(index, "tags", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Tags"
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <textarea
                                value={row.additional_packing_notes}
                                onChange={(e) =>
                                  handleAddressAccountRowChange(index, "additional_packing_notes", e.target.value)
                                }
                                disabled={isDisabled}
                                placeholder="Notes"
                                className={`${inputClass} min-h-[38px] py-1`}
                                rows={1}
                              />
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {hasId ? (
                                isEditing ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={cancelEditAddressAccount}
                                      className="text-gray-600 hover:text-gray-900 mr-2"
                                      title="Cancel"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => requestRemoveAddressAccountRow(index)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Remove row"
                                    >
                                      <Trash2 className="h-4 w-4 inline" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => startEditAddressAccount(row)}
                                      className="text-primary-600 hover:text-primary-900 mr-2"
                                      title="Edit row"
                                    >
                                      <Edit className="h-4 w-4 inline" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => requestRemoveAddressAccountRow(index)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Remove row"
                                    >
                                      <Trash2 className="h-4 w-4 inline" />
                                    </button>
                                  </>
                                )
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => requestRemoveAddressAccountRow(index)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Remove row"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={addAddressAccountRow}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Mapping
              </button>
              <button
                type="button"
                onClick={saveAddressAccountMappings}
                disabled={isSavingAddressAccount}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isSavingAddressAccount ? "Saving..." : "Save Mappings"}
              </button>
            </div>

            {addressAccountDeletePending != null && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
                role="dialog"
                aria-modal="true"
                aria-labelledby="address-account-mapping-delete-title"
              >
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-5">
                  <h3
                    id="address-account-mapping-delete-title"
                    className="text-lg font-medium text-gray-900 mb-2"
                  >
                    Delete address account mapping?
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    <span className="font-mono font-medium">
                      {addressAccountDeletePending.row.customer || "(empty)"} / {addressAccountDeletePending.row.address_code || "(empty)"}
                    </span>
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={cancelRemoveAddressAccountRow}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmRemoveAddressAccountRow}
                      disabled={isSavingAddressAccount}
                      className="px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingAddressAccount ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filters row */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Search customer shipments"
          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={handleSearch}
            className="px-3 py-2 text-sm rounded-md border bg-white hover:bg-gray-50"
          >
            Search
          </button>
          <button
            onClick={clearSearch}
            className="px-3 py-2 text-sm rounded-md border bg-white hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Sync Status Summary */}
      <div className="text-sm text-gray-700">
        <span className="mr-6">
          <span className="text-gray-500">Total Shipments:</span>{" "}
          <span className="font-semibold">{syncStatus.total_shipments ?? 0}</span>
        </span>
        <span className="mr-6">
          <span className="text-gray-500">Synced:</span>{" "}
          <span className="font-semibold">{syncStatus.synced_shipments ?? 0}</span>
        </span>
        <span className="mr-6">
          <span className="text-gray-500">Pending:</span>{" "}
          <span className="font-semibold">{syncStatus.pending_sync ?? 0}</span>
        </span>
        <span>
          <span className="text-gray-500">Last Sync:</span>{" "}
          <span className="font-semibold">{formatDate(syncStatus.last_synced_at)}</span>
        </span>
      </div>

      {/* Customer Shipments Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Customer Shipments</h3>
            {!isModeLoading && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Showing shipments for:</span>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  syncMode === "live" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"
                }`}>
                  {syncMode.toUpperCase()} mode
                </span>
              </div>
            )}
          </div>

          {isModeLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 text-primary-600 animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading sync mode...</p>
              </div>
            </div>
          ) : isShipmentsLoading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="h-8 w-8 text-primary-600 animate-spin" />
            </div>
          ) : shipments.length === 0 ? (
            <div className="text-center py-10 text-gray-500">No shipments found</div>
          ) : (
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Fulfil Shipment #</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">ShipHero Order #</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Customer</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Shipped</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {shipments.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{s.order_number}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{s.shiphero_order_number || "-"}</td>
                      <td className="px-3 py-4 text-sm text-gray-700">
                        <div className="max-w-32 truncate" title={s.customer_name || "-"}>
                          {s.customer_name || "-"}
                        </div>
                      </td>
                      <td className="px-3 py-4 text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer ${
                              s.sync_status === "pending"
                                ? "bg-yellow-100 text-yellow-800"
                                : s.sync_status === "synced"
                                ? "bg-green-100 text-green-800"
                                : s.sync_status === "error"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                            title={s.sync_error || `Status: ${s.sync_status || "unknown"}`}
                          >
                            {s.sync_status || "unknown"}
                          </span>
                          {s.acknowledged && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" title="Acknowledged in Fulfil">
                              Acknowledged
                            </span>
                          )}
                          {s.allocated && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800" title="Order allocated">
                              Allocated
                            </span>
                          )}
                          {s.packed && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" title="Order packed">
                              Packed
                            </span>
                          )}
                          {s.shipped && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800" title="Order shipped">
                              Shipped
                            </span>
                          )}
                          {s.delivered && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" title="Order delivered">
                              Delivered
                            </span>
                          )}
                          {s.failed && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" title="Failure detected">
                              Failed
                            </span>
                          )}
                          {(s.retry_count && s.retry_count > 0) ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                              {s.retry_count} {s.retry_count === 1 ? 'retry' : 'retries'}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                        {s.shipped ? (
                          <div className="flex flex-col">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800 w-fit mb-1">
                              Shipped
                            </span>
                            <span className="text-xs text-gray-500">{formatDate(s.shipped_at || null)}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-right text-sm">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openHistory(s.order_number)}
                            className="px-2 py-1 border rounded hover:bg-gray-50"
                          >
                            View
                          </button>
                          {s.sync_error ? (
                            <button
                              onClick={() => retryShipmentSync(s.id)}
                              disabled={loadingRetrySync.has(s.id)}
                              className={`px-2 py-1 border rounded text-xs font-medium ${
                                loadingRetrySync.has(s.id)
                                  ? "bg-red-100 text-red-400 cursor-not-allowed border-red-200"
                                  : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                              }`}
                            >
                              {loadingRetrySync.has(s.id) ? (
                                <div className="flex items-center">
                                  <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                                  Syncing...
                                </div>
                              ) : (
                                "Retry Sync"
                              )}
                            </button>
                          ) : (
                            <button
                              onClick={() => manualSyncStatus(s.order_number)}
                              disabled={loadingSyncNow.has(s.order_number)}
                              className={`px-2 py-1 border rounded text-xs font-medium ${
                                loadingSyncNow.has(s.order_number)
                                  ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                                  : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                              }`}
                            >
                              {loadingSyncNow.has(s.order_number) ? (
                                <div className="flex items-center">
                                  <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                                  Syncing...
                                </div>
                              ) : (
                                "Sync Now"
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Pagination */}
          {shipmentsTotalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => {
                    const p = Math.max(1, shipmentsPage - 1);
                    setShipmentsPage(p);
                    loadShipments(p, query);
                  }}
                  disabled={shipmentsPage <= 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => {
                    const p = Math.min(shipmentsTotalPages, shipmentsPage + 1);
                    setShipmentsPage(p);
                    loadShipments(p, query);
                  }}
                  disabled={shipmentsPage >= shipmentsTotalPages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{shipmentsPage}</span> of{" "}
                    <span className="font-medium">{shipmentsTotalPages}</span>
                  </p>
                </div>
                <div>
                  <nav
                    className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => {
                        const p = Math.max(1, shipmentsPage - 1);
                        setShipmentsPage(p);
                        loadShipments(p, query);
                      }}
                      disabled={shipmentsPage <= 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    {(() => {
                      const buttons = [] as number[];
                      const start = Math.max(1, Math.min(shipmentsPage - 2, Math.max(1, shipmentsTotalPages - 4)));
                      const end = Math.min(shipmentsTotalPages, start + 4);
                      for (let p = start; p <= end; p++) buttons.push(p);
                      return buttons;
                    })().map((p) => (
                      <button
                        key={p}
                        onClick={() => {
                          setShipmentsPage(p);
                          loadShipments(p, query);
                        }}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                          p === shipmentsPage
                            ? "z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                            : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        const p = Math.min(shipmentsTotalPages, shipmentsPage + 1);
                        setShipmentsPage(p);
                        loadShipments(p, query);
                      }}
                      disabled={shipmentsPage >= shipmentsTotalPages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* History Modal */}
      {showHistoryModal && historyOrderNumber && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Sync History</h3>
                <p className="text-sm text-gray-600">Order: {historyOrderNumber}</p>
              </div>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setHistoryOrderNumber(null);
                  setHistoryLogs([]);
                  setExpandedLogs(new Set());
                }}
                className="px-4 py-2 text-sm rounded-md border bg-white hover:bg-gray-50 font-medium"
              >
                Close
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {isHistoryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <RefreshCw className="h-8 w-8 text-primary-600 animate-spin mx-auto mb-2" />
                    <div className="text-gray-500 font-medium">Loading history...</div>
                  </div>
                </div>
              ) : historyLogs.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-lg mb-2">📋</div>
                  <div className="text-gray-500 font-medium">No history found</div>
                  <div className="text-gray-400 text-sm">This order has no sync history records.</div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Status</th>
                        <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Action</th>
                        <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Direction</th>
                        <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Mode</th>
                        <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Created</th>
                        <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Synced</th>
                        <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider text-right">Details</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {historyLogs.map((log) => {
                        const isOpen = expandedLogs.has(log.id);
                        const parseMaybeJson = (val?: string | null) => {
                          if (!val) return null;
                          try { return JSON.parse(val); } catch { return val; }
                        };
                        const changed = parseMaybeJson(log.changed_fields);
                        const req = parseMaybeJson(log.request_data);
                        const res = parseMaybeJson(log.response_data);
                        return (
                          <React.Fragment key={log.id}>
                            <tr className="hover:bg-gray-50">
                              <td className="px-4 py-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  log.status === 'success' ? 'bg-green-100 text-green-800' :
                                  log.status === 'error' ? 'bg-red-100 text-red-800' :
                                  log.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {formatStatus(log.status)}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">{toTitleCase(log.action)}</td>
                              <td className="px-4 py-2 text-sm text-gray-700">{formatDirection(log.sync_direction)}</td>
                              <td className="px-4 py-2 text-sm text-gray-700">{toTitleCase(log.mode)}</td>
                              <td className="px-4 py-2 text-xs text-gray-500">{formatDate(log.created_at)}</td>
                              <td className="px-4 py-2 text-xs text-gray-500">{log.synced_at ? formatDate(log.synced_at) : '—'}</td>
                              <td className="px-4 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => setExpandedLogs(prev => {
                                    const next = new Set(prev);
                                    if (next.has(log.id)) next.delete(log.id); else next.add(log.id);
                                    return next;
                                  })}
                                  className="inline-flex items-center px-3 py-1 text-sm border rounded-md bg-white hover:bg-gray-50"
                                  aria-expanded={isOpen}
                                >
                                  {isOpen ? 'Hide' : 'View'}
                                </button>
                              </td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-gray-50">
                                <td className="px-4 py-3" colSpan={7}>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                                    <div>
                                      <span className="font-medium text-gray-700">Order Number:</span>
                                      <span className="ml-2 text-gray-900">{log.order_number || 'N/A'}</span>
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-700">Customer Name:</span>
                                      <span className="ml-2 text-gray-900">{log.customer_name || 'N/A'}</span>
                                    </div>
                                    {log.customer_shipment_id && (
                                      <div>
                                        <span className="font-medium text-gray-700">Customer Shipment ID:</span>
                                        <span className="ml-2 text-gray-900">{log.customer_shipment_id}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="space-y-4">
                                    {changed && (
                                      <div>
                                        <div className="font-medium text-gray-700 mb-2">Changed Fields</div>
                                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 max-h-56 overflow-auto overflow-x-auto">
                                          {typeof changed === 'string' ? (
                                            <pre className="text-xs text-blue-800 whitespace-pre-wrap break-words font-mono">{changed}</pre>
                                          ) : (
                                            <pre className="text-xs text-blue-800 whitespace-pre font-mono">{JSON.stringify(changed, null, 2)}</pre>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {req && (
                                      <div>
                                        <div className="font-medium text-gray-700 mb-2">Request Data</div>
                                        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 max-h-56 overflow-auto overflow-x-auto">
                                          {typeof req === 'string' ? (
                                            <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words font-mono">{req}</pre>
                                          ) : (
                                            <pre className="text-xs text-gray-800 whitespace-pre font-mono">{JSON.stringify(req, null, 2)}</pre>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {res && (
                                      <div>
                                        <div className="font-medium text-gray-700 mb-2">Response Data</div>
                                        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 max-h-56 overflow-auto overflow-x-auto">
                                          {typeof res === 'string' ? (
                                            <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words font-mono">{res}</pre>
                                          ) : (
                                            <pre className="text-xs text-gray-800 whitespace-pre font-mono">{JSON.stringify(res, null, 2)}</pre>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {log.error_message && (
                                      <div>
                                        <div className="font-medium text-red-700 mb-2">Error Details</div>
                                        <div className="bg-red-50 border border-red-200 rounded-md p-3 overflow-auto">
                                          <pre className="text-xs text-red-800 whitespace-pre-wrap break-words font-mono">{log.error_message}</pre>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerShipmentsPage;
